import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { NOOR } from "@/constants/testIds/noor";
import { heroSlide, heroSlideX, trans } from "./motion";
import { BOTTLE_FRONT } from "./bottle";
import BottleDeconstruction from "./BottleDeconstruction";
import { BOTTLES_HIDDEN } from "@/lib/visibility";
import StorySection from "./StorySection";

// "The Plinth" hero — bottle rotates on Y-axis as user scrolls. No vertical motion.
const HERO_BG = {
  background:
    "radial-gradient(ellipse 55% 60% at 60% 45%, rgba(201,164,122,0.10), transparent 65%), radial-gradient(ellipse 35% 30% at 50% 80%, rgba(59,30,59,0.20), transparent 70%)",
};

// Photographic bottle delays its entrance so it cross-dissolves AS the
// deconstruction sequence is fading out (Act IV handoff). When the visitor
// skips the intro (or reduced-motion is on), it appears with a short fade.
const BOTTLE_INIT = { opacity: 0, y: 20, scale: 0.98 };
const BOTTLE_ANIM = { opacity: 1, y: 0, scale: 1 };
const BOTTLE_TRANS = trans(1.4, 2.4);
const BOTTLE_TRANS_FAST = trans(0.5, 0);

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

function HeroLead() {
  return (
    <div className="col-span-12 lg:col-span-6 z-10">
      <motion.p {...heroSlide} transition={trans(0.8, 0.2)} className="overline mb-8">
        Edition Nº 001 · 1000 Bottles · Jalisco
      </motion.p>
      <motion.h1
        {...heroSlide}
        transition={trans(1, 0.35)}
        className="font-display leading-[0.92] tracking-[-0.035em]"
        style={{
          color: "var(--noor-bone)",
          fontSize: "clamp(3.2rem, 8.4vw, 9rem)",
        }}
      >
        Architecture,<br />
        <em
          className="italic"
          style={{ color: "var(--noor-rosegold)", fontWeight: 400 }}
        >
          distilled
        </em>{" "}
        into<br />
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55em", letterSpacing: "0.02em" }}>
          750 ml.
        </span>
      </motion.h1>
      <motion.div
        {...heroSlide}
        transition={trans(0.9, 0.7)}
        className="mt-12 flex items-center gap-6"
      >
        <span className="font-mono text-xs" style={{ color: "var(--noor-vellum)" }}>
          Nº 001 / 1000
        </span>
        <span className="w-12 h-px" style={{ background: "var(--noor-rosegold)", opacity: 0.6 }} />
        <span className="font-mono text-xs" style={{ color: "var(--noor-vellum)" }}>
          Highlands · 2025
        </span>
      </motion.div>
    </div>
  );
}

function HeroBottle({ rotateY, scale, isSkipped }) {
  if (BOTTLES_HIDDEN) return null;
  return (
    <motion.div
      style={{ scale }}
      className="col-span-12 lg:col-span-5 lg:col-start-8 z-0 relative bottle-glow flex items-center justify-center"
    >
      {/* Plinth base */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[260px] h-[6px]" style={{ background: "var(--noor-rosegold)", opacity: 0.35 }} />
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[200px] h-[1px]" style={{ background: "var(--noor-rosegold)", opacity: 0.2 }} />
      {/* Act I–IV: the bottle is computed before it is photographed. */}
      <BottleDeconstruction skipped={isSkipped} />
      <motion.img
        data-testid={NOOR.heroBottle}
        src={BOTTLE_FRONT}
        alt="NOOR Tequila Blanco — Edition Nº 001"
        initial={BOTTLE_INIT}
        animate={BOTTLE_ANIM}
        transition={isSkipped ? BOTTLE_TRANS_FAST : BOTTLE_TRANS}
        style={{ rotateY, transformPerspective: 1400 }}
        className="w-full max-w-[440px] object-contain drop-shadow-[0_30px_90px_rgba(201,164,122,0.22)]"
      />
    </motion.div>
  );
}

function HeroCtas() {
  return (
    <motion.div
      {...heroSlideX}
      transition={trans(0.9, 0.9)}
      className="col-span-12 mt-16 flex flex-col sm:flex-row gap-4"
    >
      <button data-testid={NOOR.heroCtaReserve} onClick={() => scrollToId("heritage")} className="btn-solid">
        Begin the Tour
      </button>
      <button data-testid={NOOR.heroCtaDiscover} onClick={() => scrollToId("manifesto-anchor")} className="btn-ghost">
        Read the Manifesto
      </button>
    </motion.div>
  );
}

function HeroSideRails() {
  return (
    <>
      <div className="hidden lg:flex absolute left-8 top-1/2 -translate-y-1/2 flex-col items-center gap-6 z-10">
        <span className="vertical-text">Nº 001 · Small Batch</span>
        <span className="w-px h-24" style={{ background: "var(--border-subtle)" }} />
      </div>
      <div className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col items-center gap-6 z-10">
        <span className="w-px h-24" style={{ background: "var(--border-subtle)" }} />
        <span className="vertical-text">750 ml · 40% ABV</span>
      </div>
    </>
  );
}

function ScrollHint() {
  return (
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
      <span className="overline-vellum" style={{ fontSize: "0.58rem" }}>scroll</span>
      <ArrowDown size={14} color="var(--noor-rosegold)" className="animate-bounce" />
    </div>
  );
}

export default function Hero({ isSkipped = false }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  // The plinth rotates the bottle on Y-axis as the user scrolls.
  const rotateY = useTransform(scrollYProgress, [0, 1], [-12, 95]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.88]);

  return (
    <section
      data-testid={NOOR.heroSection}
      ref={ref}
      id="hero"
      className="relative min-h-screen pt-32 pb-16 overflow-hidden"
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={HERO_BG} />
      <div aria-hidden className="absolute inset-0 pointer-events-none facet-bg-vertical" style={{ opacity: 0.45 }} />
      <HeroSideRails />
      <div className="relative max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-y-6 md:gap-6 items-center min-h-[78vh]">
        <HeroLead />
        <HeroBottle rotateY={rotateY} scale={scale} isSkipped={isSkipped} />
        <HeroCtas />
      </div>
      <div className="relative max-w-[1480px] mx-auto px-6 md:px-10 mt-12 md:mt-16 pb-20">
        <StorySection testId="hero-story" />
      </div>
      <ScrollHint />
    </section>
  );
}
